import React, { useEffect, useState } from "react";
import apiClient from "api/apiClient";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Alert,
  TextField,
  CardActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchSubscription } from "state/actions/subscriptionActions";
import { useBillingCatalog, formatCatalogPrice, packDiscountPercent } from "utils/billingCatalog";

// Names and order only: the price and credits come from the server (GET /payments/plans,
// billing review P5.8), which reads the price from Stripe — the same one checkout charges.
const PLAN_KEYS = [
  { key: "starter", name: "Starter" },
  { key: "professional", name: "Professional" },
  { key: "enterprise", name: "Enterprise" },
];
const PACK_KEYS = [
  { key: "credits_5", name: "5 Credits" },
  { key: "credits_20", name: "20 Credits" },
  { key: "credits_50", name: "50 Credits" },
];

// What a credit buys, stated once for the plan cards. Runtime pricing: a run is quoted before
// it starts and charged for the computing time it actually uses, never more than the quote. The
// old line ("depends on the model and grid size") described the pricing retired on 2026-08-03.
const CREDIT_MEANING = "1 credit = 1 hour of simulation time, rounded up; each run is quoted before it starts and never costs more than its quote";

const buildSubscriptions = (catalog, error) =>
  PLAN_KEYS.map(({ key, name }) => {
    const item = catalog?.plans?.find((p) => p.key === key);
    return {
      key,
      name,
      price: formatCatalogPrice(item, { error }),
      features: [
        item ? `${item.credits} plan credits every month` : "Plan credits every month",
        "Unused plan credits do not carry over to the next month",
        CREDIT_MEANING,
      ],
    };
  });

const buildCreditPacks = (catalog, error) =>
  PACK_KEYS.map(({ key, name }) => {
    const item = catalog?.packs?.find((p) => p.key === key);
    const discount = item ? packDiscountPercent(item, catalog.packs) : 0;
    return {
      key,
      name,
      price: formatCatalogPrice(item, { error }),
      features: [
        item ? `${item.credits} simulation credits${discount ? ` (${discount}% less per credit)` : ""}` : name,
        "They do not expire at the end of a month",
      ],
    };
  });

const PricingPage = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const { catalog, error: catalogError } = useBillingCatalog();
  const subscriptions = buildSubscriptions(catalog, catalogError);
  const creditPacks = buildCreditPacks(catalog, catalogError);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showTrialInput, setShowTrialInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [trialMessage, setTrialMessage] = useState(null);
  // The plan or pack whose request is in flight. Every Subscribe / Buy button is disabled
  // while one is, so a double click or an impatient second click cannot start a second
  // subscription or a second charge (billing review B3; the server also refuses with 409).
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    dispatch(fetchSubscription());
  }, [dispatch]);

  const { data, loaded } = useSelector((state) => state.subscription);
  const plan = data?.plan;
  const pendingPlan = data?.pendingPlan;

  const handleSubscribe = async (planKey) => {
    if (pendingAction) return;
    setPendingAction(planKey);
    setMessage(null);
    let leavingForCheckout = false;
    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/start-subscription`,
        { key: planKey },
      );

      if (response.data.alreadySubscribed) {
        setMessage(response.data.message || "You are already on this plan.");
        setMessageType("info");
      } else if (response.data.success) {
        setMessage(
          response.data.upgraded
            ? "Your plan has been upgraded."
            : response.data.downgradeScheduled
              ? "Your plan will change at the end of the current period."
              : response.data.reactivated
                ? "Your subscription will continue."
                : response.data.downgradeCancelled
                  ? response.data.message || "Your scheduled plan change was cancelled."
                  : "Your subscription has been activated.",
        );
        setMessageType("success");
        dispatch(fetchSubscription());
      } else if (response.data.url) {
        leavingForCheckout = true; // keep the buttons disabled while the page navigates away
        window.location.href = response.data.url;
      } else if (response.data.message) {
        setMessage(response.data.message);
        setMessageType("info");
      }
    } catch (err) {
      console.error(err);
      // The server says why: a declined card, another billing action in progress, …
      setMessage(err?.response?.data?.message || "Failed to start subscription.");
      setMessageType("error");
    } finally {
      if (!leavingForCheckout) setPendingAction(null);
    }
  };

  const handleCancel = async () => {
    try {
      const res = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/cancel-subscription`,
        {},
      );
      setMessage(res.data.message);
      setMessageType("info");
      dispatch(fetchSubscription());
    } catch (err) {
      console.error("Cancel failed", err);
      setMessage("Failed to cancel subscription.");
      setMessageType("error");
    }
  };

  const handleBuyCredits = async (packKey) => {
    if (pendingAction) return;
    setPendingAction(packKey);
    setMessage(null);
    let leavingForCheckout = false;
    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/start-credit-purchase`,
        { packKey },
      );

      if (response.data.url) {
        leavingForCheckout = true;
        window.location.href = response.data.url;
      } else if (response.data.success) {
        setMessage(`${response.data.creditsAdded} credits have been added.`);
        setMessageType("success");
        dispatch(fetchSubscription());
      } else {
        setMessage("Something went wrong starting the payment.");
        setMessageType("error");
      }
    } catch (err) {
      console.error(err);
      setMessage(err?.response?.data?.message || "Failed to initiate credit purchase.");
      setMessageType("error");
    } finally {
      if (!leavingForCheckout) setPendingAction(null);
    }
  };

  const handleStartFreeTrial = async (code) => {
    setTrialMessage(null);
    try {
      const res = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/start-free-trial`,
        { promoCode: code },
      );

      if (res.data.success) {
        setMessage("Free trial started!");
        setMessageType("success");
        dispatch(fetchSubscription());
      } else {
        setTrialMessage("Failed to activate trial.");
      }
    } catch (err) {
      setTrialMessage(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    }
  };

  const renderSection = (title, products, actionLabel, type) => (
    <>
      <Typography variant="h4" fontWeight="bold" mt={4} mb={2}>
        {title}
      </Typography>
      <Grid container spacing={3}>
        {products.map((item) => {
          const isSubscribedPlan = plan && item.key === plan;
          const isPendingPlan = pendingPlan && item.key === pendingPlan;

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={item.name}
              sx={{ display: "flex" }}
            >
              <Card
                elevation={3}
                sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
              >
                <CardContent
                  sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
                >
                  <Typography variant="h5" gutterBottom>
                    {item.name}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {item.price}
                  </Typography>
                  <Box component="ul" sx={{ flexGrow: 1, pl: 2 }}>
                    {item.features.map((feature, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{feature}</Typography>
                      </li>
                    ))}
                  </Box>
                  {isSubscribedPlan ? (
                    <>
                      {data.scheduledPlanCancelled ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Box>
                            <Typography sx={{ mb: 1 }}>
                              You’ve cancelled your <strong>{item.name}</strong>{" "}
                              subscription.
                            </Typography>
                            <Typography sx={{ mb: 1 }}>
                              Subscription ends on:{" "}
                              <strong>
                                {new Date(data.expiresAt).toLocaleDateString()}
                              </strong>
                            </Typography>
                            <Typography sx={{ mb: 1 }}>
                              Plan credits left:{" "}
                              <strong>{data.planCredits}</strong>
                            </Typography>
                            <Typography>
                              Changed your mind? Resubscribe to keep it — nothing is
                              charged until your next renewal date.
                            </Typography>
                          </Box>
                          {/* A cancellation is undoable until the period ends (billing review
                              Phase 2): this used to be a dead end with no way back. */}
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ mt: 1.5 }}
                            disabled={Boolean(pendingAction)}
                            onClick={() => handleSubscribe(item.key)}
                          >
                            {pendingAction === item.key ? "Working…" : "Resubscribe"}
                          </Button>
                        </Alert>
                      ) : data.cancelAtPeriodEnd ? (
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          sx={{ mt: 1 }}
                          onClick={() => handleSubscribe(item.key)} // resubscribe
                        >
                          Resubscribe
                        </Button>
                      ) : (
                        <>
                          {data.pendingPlan && (
                            // A downgrade is scheduled for the end of the period: let the user
                            // stay on this plan instead (the server releases the schedule).
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              sx={{ mt: 1 }}
                              disabled={Boolean(pendingAction)}
                              onClick={() => handleSubscribe(item.key)}
                            >
                              {pendingAction === item.key ? "Working…" : `Keep ${item.name}`}
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            sx={{ mt: 1 }}
                            onClick={() => setConfirmCancel(true)}
                          >
                            Cancel Subscription
                          </Button>
                        </>
                      )}

                      {!data.scheduledPlanCancelled && data?.expiresAt && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {data.cancelAtPeriodEnd
                            ? `Subscription ends on: ${new Date(
                                data.expiresAt,
                              ).toLocaleDateString()}`
                            : data.pendingPlan
                              ? `Switches to ${
                                  data.pendingPlan
                                } plan on: ${new Date(
                                  data.expiresAt,
                                ).toLocaleDateString()}`
                              : `Renews on: ${new Date(
                                  data.expiresAt,
                                ).toLocaleDateString()}`}
                        </Typography>
                      )}

                      {data?.planCredits !== undefined &&
                        !data.scheduledPlanCancelled && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            Plan credits left: {data.planCredits}
                          </Typography>
                        )}

                      {["past_due", "unpaid"].includes(data?.status) && (
                        // A renewal payment is failing: the credits left from the last paid month
                        // stay usable, but this month's allowance is added only once the payment
                        // succeeds (billing review, decision D4 — option B, Ali 2026-09-28).
                        <Alert severity="warning" variant="filled" sx={{ mt: 1.5, textAlign: "left" }}>
                          Your latest payment did not go through, so this month&apos;s credits
                          have not been added yet. You can still use the credits you have.
                          Update your card under Payment Methods to receive them.
                        </Alert>
                      )}
                    </>
                  ) : isPendingPlan ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Downgrade scheduled for{" "}
                      {data?.expiresAt &&
                        new Date(data.expiresAt).toLocaleDateString()}
                    </Typography>
                  ) : data.scheduledPlanCancelled && type === "subscription" ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      You can’t subscribe to a new plan until your current one
                      expires on{" "}
                      <strong>
                        {data?.expiresAt &&
                          new Date(data.expiresAt).toLocaleDateString()}
                      </strong>
                      .
                    </Alert>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ mt: 2 }}
                      disabled={Boolean(pendingAction)}
                      onClick={() =>
                        type === "subscription"
                          ? handleSubscribe(item.key)
                          : handleBuyCredits(item.key)
                      }
                    >
                      {pendingAction === item.key ? "Working…" : actionLabel}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        {type === "subscription" && plan === "free_trial" && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                backgroundColor: "#e0f7fa",
                color: "#006064",
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🎁 Free Trial Active
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  You are currently using a free trial plan.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Trial expires on:{" "}
                  <strong>
                    {new Date(data.expiresAt).toLocaleDateString()}
                  </strong>
                </Typography>
                <Typography variant="body2">
                  Plan credits left: <strong>{data.planCredits}</strong>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {type === "subscription" && !plan && !pendingPlan && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                backgroundColor: "#1e1e1e",
                color: "white",
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🎁 Start Free Trial
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Have a promo code? Enter it to start a free trial with the
                  credits the code includes.
                </Typography>
                {showTrialInput && (
                  <>
                    <TextField
                      fullWidth
                      label="Promo Code"
                      variant="outlined"
                      size="small"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      sx={{
                        mt: 2,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#fff",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ccc",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#fff",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "#fff",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#fff",
                        },
                        input: { color: "#fff" },
                      }}
                    />
                    {trialMessage && (
                      <Typography variant="body2" color="error" mt={1}>
                        {trialMessage}
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
              <CardActions
                sx={{
                  px: 2,
                  gap: 1,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                {showTrialInput ? (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ flex: 1 }}
                      onClick={() => handleStartFreeTrial(promoCode.trim())}
                      disabled={!promoCode.trim()}
                    >
                      Activate Free Trial
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      sx={{ flex: 1 }}
                      onClick={() => {
                        setShowTrialInput(false);
                        setPromoCode("");
                        setTrialMessage("");
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => setShowTrialInput(true)}
                  >
                    Use Promo Code
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        )}
      </Grid>
      {confirmCancel && type === "subscription" && (
        <Box mt={2}>
          <Alert
            severity="warning"
            sx={{ width: "100%" }}
            action={
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => setConfirmCancel(false)}
                  sx={{
                    borderColor: "#888",
                    color: "#fff",
                    "&:hover": { borderColor: "#ccc" },
                  }}
                >
                  No, keep it
                </Button>
                <Button
                  color="error"
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setConfirmCancel(false);
                    handleCancel();
                  }}
                >
                  Yes, cancel
                </Button>
              </Box>
            }
          >
            {data.pendingPlan
              ? "By cancelling now, you won’t be able to subscribe to a new plan until the end of the current billing period."
              : "Are you sure you want to cancel your subscription?"}
          </Alert>
        </Box>
      )}
    </>
  );

  return (
    <Box p={embedded ? 0 : 4}>
      {message && (
        <Alert
          severity={messageType}
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setMessage(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {message}
        </Alert>
      )}

      {!loaded ? (
        <Typography variant="h6">
          Loading your subscription details...
        </Typography>
      ) : (
        <>
          {renderSection(
            "Monthly Subscription Plans",
            subscriptions,
            "Subscribe",
            "subscription",
          )}

          <Divider sx={{ my: 6 }} />

          {/* Desktop licence of the PERSONAL workspace — its own entitlement, separate from
              credits (docs/desktop-license-entitlement-plan.md §6.1). Granted by PGL. */}
          <Box mt={4} mb={2} data-testid="plan-desktop-license">
            <Typography variant="h4" fontWeight="bold" mb={2}>
              Desktop Licence
            </Typography>
            <Card elevation={3} sx={{ maxWidth: 420 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography fontWeight={600}>
                  {data?.desktopLicense?.status === "active"
                    ? `Active until ${new Date(data.desktopLicense.expiresAt).toLocaleDateString()}`
                    : data?.desktopLicense?.status === "expired"
                      ? `Expired ${new Date(data.desktopLicense.expiresAt).toLocaleDateString()}`
                      : "No desktop licence"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Lets you prepare simulation packages here and run them on your own computer with
                  the CityDigitalTwin desktop app. No simulation credits are used.
                </Typography>
                {data?.desktopLicense?.status !== "active" && (
                  <Button
                    size="small"
                    href="mailto:contact@greenleaves.app?subject=CityDigitalTwin%20Desktop%20license"
                    // This card is DARK (MUI palette mode): the default primary ink was nearly
                    // invisible on it. Same light green as the org Billing tab's licence link.
                    sx={{ mt: 1, px: 0, color: "#7ff0b6", fontWeight: 600 }}
                  >
                    {data?.desktopLicense?.status === "expired" ? "Renew — contact us" : "Get a desktop licence — contact us"}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ my: 6 }} />

          <Box mt={4} mb={2}>
            <Typography variant="h4" fontWeight="bold" mb={4}>
              Buy Simulation Credits
            </Typography>

            {data?.credits !== undefined && (
              <Card elevation={3} sx={{ maxWidth: 320 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Remaining Credits:
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color="secondary"
                    >
                      {data.credits}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>

          {renderSection("", creditPacks, "Buy Credits", "credits")}
        </>
      )}
    </Box>
  );
};

export default PricingPage;
